/**
 * Token storage, scoped by API URL.
 *
 * Same rule as the mini program: a JWT issued by localhost is not valid
 * against production, so the key includes a hash of the API origin. Switching
 * VITE_API_URL then reads a different key instead of sending a token the other
 * backend will reject.
 */
import { env } from './env'

const hash = (value: string) => {
  let h = 0
  for (let i = 0; i < value.length; i += 1) h = ((h << 5) - h + value.charCodeAt(i)) >>> 0
  return h.toString(36)
}

const TOKEN_KEY = `tc_jwt:${hash(env.apiUrl || 'dev')}`
/**
 * The refresh token is scoped the same way and cleared with the access token —
 * they are two halves of one session, and half a session is a bug generator.
 */
const REFRESH_KEY = `tc_refresh:${hash(env.apiUrl || 'dev')}`

export const getToken = (): string => {
  try { return localStorage.getItem(TOKEN_KEY) || '' } catch { return '' }
}
export const setToken = (token: string) => {
  try { localStorage.setItem(TOKEN_KEY, token) } catch { /* private mode */ }
}

export const getRefreshToken = (): string => {
  try { return localStorage.getItem(REFRESH_KEY) || '' } catch { return '' }
}
export const setRefreshToken = (token: string) => {
  try {
    if (token) localStorage.setItem(REFRESH_KEY, token)
    else localStorage.removeItem(REFRESH_KEY)
  } catch { /* private mode */ }
}

/** Ends the session locally. ALWAYS drops both halves. */
export const clearToken = () => {
  try {
    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(REFRESH_KEY)
  } catch { /* private mode */ }
}

/** Wholesale wipe, for the dev "force sign out". */
export const clearAllStorage = () => {
  try { localStorage.clear() } catch { /* private mode */ }
}

export const readJson = <T>(key: string, fallback: T): T => {
  try {
    const raw = localStorage.getItem(key)
    return raw ? (JSON.parse(raw) as T) : fallback
  } catch { return fallback }
}
export const writeJson = (key: string, value: unknown) => {
  try { localStorage.setItem(key, JSON.stringify(value)) } catch { /* noop */ }
}
